// src/components/AppSidebar.tsx
//
// Global left navigation for the app shell: the module list that used to be the
// LandingPage tile grid, now a pinnable/sliding side panel where each module
// expands to its sub-tabs. Reuses useSidebarPref for the pin/hover behavior so
// it matches the in-page SideNav. Feature-gated via useModuleAccess.

import { useState } from 'react';
import { ChevronRight, Home } from 'lucide-react';
import type { Page } from '../App';
import { NAV_MODULES } from '../lib/appNav';
import { setSection, getSection, useSectionsVersion } from '../lib/sectionStore';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useSidebarPref } from '../hooks/useSidebarPref';

interface AppSidebarProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
}

export default function AppSidebar({ currentPage, onNavigate }: AppSidebarProps) {
  const { canAccess, tenantHas } = useModuleAccess();
  const { pinned, togglePin, expanded, containerHandlers } = useSidebarPref();
  const [openModules, setOpenModules] = useState<Set<string>>(new Set());
  useSectionsVersion(); // re-render when the active section changes

  const modules = NAV_MODULES.filter((m) => canAccess(m.id, { sysadminOnly: m.sysadminOnly }));

  const goModule = (page: Page, hasSub: boolean, id: string) => {
    if (hasSub) {
      setOpenModules((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }
    onNavigate(page);
  };

  const goSubTab = (page: Page, section: string) => {
    setSection(page, section);
    if (currentPage !== page) onNavigate(page);
  };

  return (
    <aside
      {...containerHandlers}
      className="hidden md:block self-stretch shrink-0 relative z-30"
      style={{ width: pinned ? '14rem' : '3.5rem' }}
    >
      <div
        className={`absolute inset-y-0 left-0 flex flex-col bg-slate-900 border-r border-slate-700/50 transition-[width] duration-150 ${
          expanded ? 'w-56 shadow-xl shadow-black/30' : 'w-14'
        }`}
      >
        {/* Home + pin */}
        <div className="flex items-center justify-between h-12 px-2 border-b border-slate-700/50 shrink-0">
          <button
            onClick={() => onNavigate('landing')}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-slate-300 hover:bg-slate-800/60 transition-colors ${
              currentPage === 'landing' ? 'text-white' : ''
            }`}
            title="Home"
          >
            <Home size={18} className="shrink-0" />
            {expanded && <span className="text-sm font-semibold">Home</span>}
          </button>
          {expanded && (
            <button
              onClick={togglePin}
              className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-slate-800/60 transition-colors text-xs"
              title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
            >
              {pinned ? '«' : '»'}
            </button>
          )}
        </div>

        {/* Modules */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2">
          {modules.map((m) => {
            const Icon = m.icon;
            const active = currentPage === m.page;
            const hasSub = !!m.subTabs?.length;
            const isOpen = openModules.has(m.id) || active;
            const subs = (m.subTabs ?? []).filter((s) =>
              (s.feature === null || canAccess(s.feature)) && !(s.hideWhenTenant && tenantHas(s.hideWhenTenant)));
            const activeSub = getSection(m.page);
            return (
              <div key={m.id}>
                <button
                  onClick={() => goModule(m.page, hasSub, m.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                    active ? 'bg-blue-500/15 text-white border-l-2 border-blue-400' : 'text-slate-300 hover:bg-slate-800/50 border-l-2 border-transparent'
                  }`}
                  title={m.label}
                >
                  <Icon size={18} />
                  {expanded && <span className="text-sm flex-1 truncate">{m.label}</span>}
                  {expanded && hasSub && (
                    <ChevronRight size={15} className={`text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  )}
                </button>
                {expanded && hasSub && isOpen && (
                  <div className="pb-1">
                    {subs.map((s) => (
                      <button
                        key={s.section}
                        onClick={() => goSubTab(m.page, s.section)}
                        className={`w-full text-left pl-11 pr-3 py-1.5 text-[13px] transition-colors truncate ${
                          active && activeSub === s.section
                            ? 'text-white bg-blue-500/10'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800/40'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
